import React, { useEffect, useMemo, useRef, useState } from "react";
import { Divider, Grid } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import pxToRem from "assets/theme/functions/pxToRem";
import { Feature } from "flagged";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageTitle from "examples/NewDesign/PageTitle";
import DataTable from "examples/Tables/DataTable";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import CustomButton from "examples/NewDesign/CustomButton";
import NewProject from "examples/modal/NewProject/NewProject";
import DeleteModal from "examples/modal/deleteModal/deleteModal";
import BasicModal from "examples/modal/BasicModal/BasicModal";
import FullScreenImageComponent from "components/ViewFullImage/ViewImage";
import ImageUpload from "components/ImageUpload/imageUpload";
import FTextField from "components/Form/FTextField";
import FDropdown2 from "components/Dropdown/fDropdown2";
import MultiSelectDropdown from "components/Dropdown/MultiSelectDropdown";
import MDBox from "components/MDBox";

import Constants, {
  ButtonTitles,
  Colors,
  Common,
  Icons,
  ModalContent,
  PageTitles,
  DropdownOptions,
  defaultData,
  FeatureTags,
} from "utils/Constants";
import {
  createSafetyAlert,
  deleteSafetyAlert,
  getSafetyAlerts,
  updateSafetyAlert,
} from "redux/Thunks/SafetyAlerts";
import getProfileFunctions from "redux/Thunks/ProfileFunctions";
import Validators from "utils/Validations";
import AcknowledgeMemberData from "layouts/wfmwizard/DailyActivity/setupProject/ProjectTableData/AcknowledgeMemberData";
import { openSnackbar } from "redux/Slice/Notification";
import SafetyAlertData from "./data/SafetyAlertData";

const initialForm = {
  title: "",
  documentNumber: "",
  version: "",
  type: "",
  document: [],
  profileFunctions: [],
};

function SafetyAlert() {
  const dispatch = useDispatch();
  const configState = useSelector((state) => state.config);

  const [alerts, setAlerts] = useState([]);
  const [modaltype, setModalType] = useState(Common.NEW);
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [deleteData, setDeleteData] = useState({
    open: false,
    id: "",
    loading: false,
  });
  const [acknowledgeData, setAcknowledgeData] = useState({});
  const [openAcknowledgeModal, setOpenAcknowledgeModal] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(Constants.PENDING);
  const [tablePagination, setTablePagination] = useState({
    page: 0,
    perPage: defaultData.PER_PAGE,
  });
  const [next, setNext] = useState(0);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [editId, setEditId] = useState("");
  const isMounted = useRef(false);
  const [profileFunctionsList, setProfileFunctionsList] = useState([]);

  const permission = useMemo(() => {
    const screen = configState.screens.find(
      (item) => item.id === defaultData.GLOBAL_SAFETY_ALERT_SCREEN_ID
    );
    return screen?.screensInfo?.agreement || {};
  }, [configState]);

  const resetForm = () => {
    setForm(initialForm);
    setErrors({});
    setEditId("");
  };

  const handleOpenModal = (type = Common.NEW) => {
    setModalType(type);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalType(Common.NEW);
    resetForm();
  };

  const handleOpenDeleteModal = (id) => {
    setDeleteData({ open: true, id, loading: false });
  };

  const handleCloseDeleteModal = () => {
    setDeleteData({ open: false, id: "", loading: false });
  };

  const handlePreview = (url) => {
    if (!url) return;
    if (url.includes(".pdf")) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      setFullScreenImage(url);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleProfileFunctionChange = (name, values) => {
    setForm((prev) => ({ ...prev, [name]: values }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleImageChange = (imageValues) => {
    setForm((prev) => ({ ...prev, document: imageValues }));
    setErrors((prev) => ({ ...prev, document: "" }));
  };

  const handleImageCancel = (updatedImageUrl) => {
    setForm((prev) => ({
      ...prev,
      document: updatedImageUrl,
    }));
  };

  const fetchProfileFunctions = async () => {
    const params = new URLSearchParams({
      page: 0,
      perPage: defaultData.PER_PAGE,
    });
    const res = await dispatch(getProfileFunctions(params.toString()));
    if (!isMounted.current) return;
    if (res?.payload?.status === Common.API_STATUS_200 || res?.payload?.status === 200) {
      const mapped =
        res.payload.data.data?.map((pf) => ({
          [Constants.MONGOOSE_ID]: pf[Constants.MONGOOSE_ID],
          title: pf.name,
        })) || [];
      setProfileFunctionsList(mapped);
    }
  };

  const fetchAlerts = async () => {
    setLoadingStatus(Constants.PENDING);
    const params = new URLSearchParams({
      page: 0,
      perPage: tablePagination.perPage,
      sort: "desc",
    });
    const res = await dispatch(getSafetyAlerts(params));
    if (!isMounted.current) return;
    setLoadingStatus(Constants.FULFILLED);
    if (res.payload.status === Common.API_STATUS_200) {
      setAlerts(res.payload.data.data || []);
      setNext(0);
      setTablePagination((prev) => ({ ...prev, page: 0 }));
    } else {
      dispatch(
        openSnackbar({
          message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  const handleReload = async () => {
    await fetchAlerts();
  };

  const validateForm = () => {
    const tempErrors = {};
    const trimmedTitle = form.title?.trim();
    const trimmedNumber = form.documentNumber?.trim();
    const trimmedVersion = form.version?.trim();

    const titleError = Validators.validate("basic2", trimmedTitle);
    const numberError = Validators.validate("basic2", trimmedNumber);
    const versionError = Validators.validate("basic2", trimmedVersion);
    const documentError = Validators.validate("image", form.document || []);
    const profileFunctionError =
      Array.isArray(form.profileFunctions) && form.profileFunctions.length > 0
        ? ""
        : Constants.REQUIRED;

    if (titleError) tempErrors.title = titleError;
    if (numberError) tempErrors.documentNumber = numberError;
    if (versionError) tempErrors.version = versionError;
    if (!form.type) tempErrors.type = Constants.REQUIRED;
    if (documentError) tempErrors.document = documentError;
    if (profileFunctionError) tempErrors.profileFunctions = profileFunctionError;
    setErrors((prev) => ({ ...prev, ...tempErrors }));
    return Object.keys(tempErrors).length === 0;
  };

  const formatPayload = () => {
    const documentData = Array.isArray(form.document) ? form.document[0] : form.document;
    return {
      title: form.title?.trim(),
      documentNumber: form.documentNumber?.trim(),
      version: form.version?.trim(),
      type: form.type,
      document: documentData,
      profileFunctions: form.profileFunctions,
    };
  };

  const handleCreate = async () => {
    const isValid = validateForm();
    if (!isValid) return;
    setDisableSubmit(true);
    setLoading(true);
    const payload = formatPayload();
    const res = await dispatch(createSafetyAlert(payload));
    if (!isMounted.current) return;
    setDisableSubmit(false);
    setLoading(false);
    if (res.payload.status === Common.API_STATUS_200) {
      dispatch(
        openSnackbar({
          message: Constants.SAFETY_ALERT_CREATE_SUCCESS,
          notificationType: Constants.NOTIFICATION_SUCCESS,
        })
      );
      handleCloseModal();
      await fetchAlerts();
    } else {
      dispatch(
        openSnackbar({
          message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  const handleUpdate = async () => {
    const isValid = validateForm();
    if (!isValid || !editId) return;
    setDisableSubmit(true);
    setLoading(true);
    const payload = formatPayload();
    const res = await dispatch(updateSafetyAlert({ id: editId, body: payload }));
    if (!isMounted.current) return;
    setDisableSubmit(false);
    setLoading(false);
    if (res.payload.status === Common.API_STATUS_200) {
      dispatch(
        openSnackbar({
          message: Constants.SAFETY_ALERT_UPDATE_SUCCESS,
          notificationType: Constants.NOTIFICATION_SUCCESS,
        })
      );
      handleCloseModal();
      await fetchAlerts();
    } else {
      dispatch(
        openSnackbar({
          message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  const handleDelete = async () => {
    setDeleteData((prev) => ({ ...prev, loading: true }));
    const res = await dispatch(deleteSafetyAlert(deleteData.id));
    if (!isMounted.current) return;
    if (res.payload.status === Common.API_STATUS_200) {
      dispatch(
        openSnackbar({
          message: Constants.SAFETY_ALERT_DELETE_SUCCESS,
          notificationType: Constants.NOTIFICATION_SUCCESS,
        })
      );
      handleCloseDeleteModal();
      await fetchAlerts();
    } else {
      dispatch(
        openSnackbar({
          message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
      setDeleteData((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleEdit = (alert) => {
    const activeDocument =
      alert.document?.find((doc) => doc.isActive) || alert.document?.[alert.document.length - 1];
    setForm({
      title: alert.title || "",
      documentNumber: alert.documentNumber || "",
      version: activeDocument?.version || "",
      type: alert.type || "",
      document: activeDocument
        ? [
            {
              url: activeDocument.url,
              name: activeDocument.name,
              version: activeDocument.version,
            },
          ]
        : [],
      profileFunctions:
        alert.profileFunctions?.map((pf) =>
          pf[Constants.MONGOOSE_ID] ? pf[Constants.MONGOOSE_ID] : pf
        ) || [],
    });
    setErrors({});
    setEditId(alert[Constants.MONGOOSE_ID]);
    handleOpenModal(Common.UPDATE);
  };

  const handleViewAcknowledgements = (alert) => {
    setAcknowledgeData(alert);
    setOpenAcknowledgeModal(true);
  };

  const handleCloseAcknowledgeModal = () => {
    setOpenAcknowledgeModal(false);
    setAcknowledgeData({});
  };

  const handleCurrentPage = (page) => {
    setTablePagination((prev) => ({ ...prev, page }));
  };

  const handleTablePagination = async () => {
    const params = new URLSearchParams({
      page: next + 1,
      perPage: tablePagination.perPage,
      sort: "desc",
    });

    const res = await dispatch(getSafetyAlerts(params));
    if (!isMounted.current) return;
    if (res.payload.status === Common.API_STATUS_200) {
      const data = res.payload.data.data || [];
      setAlerts((prev) => [...prev, ...data]);
      setNext(data.length > 0 ? next + 1 : next);
      setTablePagination((prev) => ({ ...prev, page: next + 1 }));
    }
  };

  useEffect(() => {
    isMounted.current = true;
    fetchAlerts();
    fetchProfileFunctions();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const { safetyAlertColumns, safetyAlertRows } = SafetyAlertData(
    alerts,
    handleEdit,
    handleOpenDeleteModal,
    handlePreview,
    handleViewAcknowledgements,
    permission
  );

  const { acknowledgeMemberColumns, acknowledgeMemberRows } = AcknowledgeMemberData(
    acknowledgeData?.docReviews || []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Feature name={FeatureTags.GLOBAL_SAFETY_ALERT}>
        <MDBox
          display="flex"
          flexDirection={{ md: "row", sm: "column" }}
          justifyContent={{ md: "space-between" }}
          alignItems={{ lg: "space-between", sm: "center" }}
          mt={1}
          mb={2}
        >
          <PageTitle title={PageTitles.SAFETY_ALERT_GLOBAL} subTitle="" />
          <MDBox mt={{ lg: 0, sm: 2 }} display="flex" flexWrap="wrap">
            {permission?.create && (
              <CustomButton
                title={ButtonTitles.SAFETY_ALERT}
                icon={Icons.NEW}
                background={Colors.PRIMARY}
                color={Colors.WHITE}
                openModal={() => handleOpenModal(Common.NEW)}
              />
            )}
            <Divider
              orientation="vertical"
              sx={{
                backgroundColor: "var(--gray-300, #D0D5DD)",
                height: "auto",
                marginLeft: pxToRem(16),
                marginRight: 0,
              }}
            />
            <BasicButton
              title={ButtonTitles.RELOAD}
              icon={Icons.RELOAD}
              background={Colors.WHITE}
              action={handleReload}
              border
              color={Colors.BLACK}
            />
          </MDBox>
        </MDBox>

        <MDBox mt={1} mb={2}>
          <DataTable
            table={{ columns: safetyAlertColumns, rows: safetyAlertRows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: defaultData.PER_PAGE }}
            showTotalEntries={false}
            noEndBorder
            loading={loadingStatus}
            currentPage={tablePagination.page}
            handleTablePagination={handleTablePagination}
            handleCurrentPage={handleCurrentPage}
          />
        </MDBox>

        <NewProject
          title={
            modaltype === Common.NEW
              ? ModalContent.NEW_DOCUMENT_TITLE
              : ModalContent.EDIT_DOCUMENT_TITLE
          }
          openNewProject={openModal}
          handleCloseNewProject={handleCloseModal}
          handleSave={modaltype === Common.NEW ? handleCreate : handleUpdate}
          actionButton={loading ? ButtonTitles.LOADING : ButtonTitles.SUBMIT}
          disabled={disableSubmit}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FTextField
                label="Document Name*"
                name="title"
                placeholder="Document Name"
                type="text"
                error={Boolean(errors.title)}
                helperText={errors.title}
                value={form.title}
                handleChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <FTextField
                label="Document Number*"
                name="documentNumber"
                placeholder="Document Number"
                type="text"
                error={Boolean(errors.documentNumber)}
                helperText={errors.documentNumber}
                value={form.documentNumber}
                handleChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FTextField
                label="Version*"
                name="version"
                placeholder="Version"
                type="text"
                error={Boolean(errors.version)}
                helperText={errors.version}
                value={form.version}
                handleChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6} mt={-1}>
              <FDropdown2
                label="Alert Type*"
                name="type"
                value={form.type}
                options={DropdownOptions.PROJECT_DOCUMENT_TYPES_AT_SETUP_PROJECT.slice(2)}
                handleChange={handleDropdownChange}
                error={Boolean(errors.type)}
                helperText={errors.type}
                addClearIcon
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <MultiSelectDropdown
                label="Profile Functions*"
                name="profileFunctions"
                values={form.profileFunctions}
                menu={profileFunctionsList}
                handleChange={handleProfileFunctionChange}
                error={Boolean(errors.profileFunctions)}
                helperText={errors.profileFunctions}
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox mt={2}>
                <ImageUpload
                  label={modaltype !== Common.NEW ? Common.UPDATE_DOCUMENT : Common.UPLOAD_DOCUMENT}
                  name="document"
                  data={form.document}
                  onImageUpload={handleImageChange}
                  onImageCancel={(updatedImageUrl) => handleImageCancel(updatedImageUrl)}
                  error={errors.document}
                  helperText={errors.document}
                  type="Safety_Alert"
                  formats={[
                    Common.IMAGE_JPEG,
                    Common.IMAGE_JPG,
                    Common.IMAGE_PNG,
                    Common.IMAGE_SVG,
                    Common.PDF_FILE,
                  ]}
                  acceptType="image/*, application/pdf"
                  maxImageCount={1}
                  imageTypeError={Constants.IMAGE_UPLOAD_ERROR}
                  sizeUpdate
                />
              </MDBox>
            </Grid>
          </Grid>
        </NewProject>
      </Feature>
      <DeleteModal
        open={deleteData.open}
        title="Delete Safety Alert"
        message="Are you sure you want to delete?"
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDelete}
        loading={deleteData.loading}
      />

      <FullScreenImageComponent
        fullScreenImage={fullScreenImage}
        handleCloseFullView={() => setFullScreenImage(null)}
        src={fullScreenImage}
      />

      <BasicModal
        title={acknowledgeData?.title || "Acknowledgements"}
        open={openAcknowledgeModal}
        handleClose={handleCloseAcknowledgeModal}
        displayButtons={false}
        py={3}
        minWidth={900}
      >
        <DataTable
          table={{ columns: acknowledgeMemberColumns, rows: acknowledgeMemberRows }}
          isSorted={false}
          entriesPerPage={{ defaultValue: defaultData.PER_PAGE_4 }}
          showTotalEntries={false}
          pagination={{ variant: "gradient", color: "info" }}
          loading={Constants.FULFILLED}
          licenseRequired
        />
      </BasicModal>
    </DashboardLayout>
  );
}

export default SafetyAlert;
